import { useId, useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Logout, Settings, UnfoldMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { useAuthContext } from "@/context/AuthContext";
import { useTenantFeatures } from "@/context/TenantFeaturesContext";

function getInitials(displayName: string, email: string): string {
  const source = displayName.trim() || email.trim();
  if (!source) {
    return "?";
  }

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
}

export function SidebarUserMenu({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const { userSummary, logout } = useAuthContext();
  const { config } = useTenantFeatures();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const idBase = useId();
  const triggerId = `${idBase}-trigger`;
  const menuId = `${idBase}-menu`;

  if (!userSummary) {
    return null;
  }

  const { displayName, email } = userSummary;
  const tenantLabel = config?.display_name ?? "";
  const initials = getInitials(displayName, email);

  return (
    <Box sx={{ px: collapsed ? 0.5 : 1, pb: 1.5, pt: 0.5, flexShrink: 0 }}>
      <Divider sx={{ borderColor: alpha("#fff", 0.08), mb: 1 }} />

      <ButtonBase
        id={triggerId}
        className="sidebar-user-menu-trigger"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? menuId : undefined}
        // Render as a div (ButtonBase adds role="button", tabIndex and
        // Enter/Space handling) so the block layout and children stay as before.
        component="div"
        disableRipple
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1,
          px: collapsed ? 0 : 1,
          py: 0.75,
          borderRadius: 1.25,
          cursor: "pointer",
          color: "#fff !important",
          "& .MuiTypography-root": {
            color: "#fff !important",
          },
          "& .MuiTypography-root:last-of-type": {
            color: "rgba(255, 255, 255, 0.75) !important",
          },
          "&:hover": {
            backgroundColor: alpha("#fff", 0.12),
          },
          "&.Mui-focusVisible": {
            backgroundColor: alpha("#fff", 0.12),
            outline: `2px solid ${alpha("#fff", 0.6)}`,
            outlineOffset: -2,
          },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: "0.75rem",
            fontWeight: 700,
            bgcolor: "#2e7d4f",
          }}
        >
          {initials}
        </Avatar>
        {!collapsed && (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: "#fff !important",
                }}
              >
                {displayName}
              </Typography>
              {tenantLabel ? (
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.65rem",
                    lineHeight: 1.2,
                    color: "rgba(255, 255, 255, 0.75) !important",
                  }}
                >
                  {tenantLabel}
                </Typography>
              ) : null}
            </Box>
            {/* Decorative: the whole row is the button (no nested interactive element). */}
            <Box
              component="span"
              aria-hidden
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "#fff !important",
                p: 0.25,
              }}
            >
              <UnfoldMore fontSize="small" />
            </Box>
          </>
        )}
      </ButtonBase>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          list: { "aria-labelledby": triggerId },
          paper: {
            sx: { minWidth: 240, borderRadius: 2, mt: -1 },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#2e7d4f" }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {displayName}
            </Typography>
            {(tenantLabel || email) && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {tenantLabel || email}
              </Typography>
            )}
          </Box>
        </Box>

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate("/settings");
          }}
          sx={{
            py: 1,
            alignItems: "center",
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            logout();
          }}
          sx={{ py: 1, alignItems: "center" }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Log out" />
        </MenuItem>
      </Menu>
    </Box>
  );
}
